import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect } from "react";
import { useActiveColorScheme } from "@m/alchemy-theme";
import { useStringMap } from "../utilities/string-map/useStringMap";
import { CloseIcon } from "../icons/CloseIcon";
import { ConfirmIcon } from "../icons/ConfirmIcon";
import { EditIcon } from "../icons/EditIcon";
import { ErrorIcon } from "../icons/ErrorIcon";
import { ImportantIcon } from "../icons/ImportantIcon";
import { InformationIcon } from "../icons/InformationIcon";
import { UserIcon } from "../icons/UserIcon";
import { WarningIcon } from "../icons/WarningIcon";
import * as Styled from "./MessageBanner.styled";
import { stringMapDefault } from "./MessageBanner.stringMap";
export const MESSAGE_LEVELS = {
    error: "expressiveNegative",
    inactive: "expressiveNeutral",
    informative: "expressiveInformative",
    negative: "expressiveNegative",
    neutral: "expressiveWarning",
    positive: "expressivePositive",
    service: "expressiveWarning",
    warning: "expressiveWarning"
};
export const MESSAGE_ICONS = {
    confirm: ConfirmIcon,
    edit: EditIcon,
    error: ErrorIcon,
    important: ImportantIcon,
    informative: InformationIcon,
    user: UserIcon,
    warning: WarningIcon
};
const defaultType = "warning";
const componentName = "MessageBanner";
export const MessageBanner = forwardRef((props, ref) => {
    /* istanbul ignore next */
    const { autoDismissDelay = 4000, children, content, icon, 
    /* cspell: disable */
    isDismissable, isDismissible = isDismissable, 
    /* cspell: enable */
    isPolite, level = "neutral", onDismiss, shouldAutoDismiss, size = "default", stringMap: stringMapProp, title, type = defaultType, ...otherProps } = props;
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    useEffect(() => {
        if (shouldAutoDismiss) {
            const autoDismissTimer = setTimeout(() => onDismiss && onDismiss(), autoDismissDelay);
            return () => clearTimeout(autoDismissTimer);
        }
        return undefined;
    }, [autoDismissDelay, onDismiss, shouldAutoDismiss]);
    const closeOnEsc = (event) => {
        if (event.key === "Escape" && onDismiss) {
            onDismiss();
        }
    };
    const currentPalette = MESSAGE_LEVELS[level];
    const Icon = MESSAGE_ICONS[type];
    const activeColorScheme = useActiveColorScheme();
    return (_jsxs(Styled.MessageBanner, { ref: ref, "data-testid": "MessageBanner", palette: currentPalette, onKeyDown: isDismissible ? closeOnEsc : undefined, size: size, ...otherProps, children: [_jsxs(Styled.Alert, { isPolite: isPolite, children: [_jsx(Styled.IconWrapper, { palette: currentPalette, size: size, children: icon ?? _jsx(Icon, { palette: currentPalette, "aria-label": stringMap.ariaLabelIcon }) }), title && (_jsx(Styled.Title, { size: size, activeColorScheme: activeColorScheme, children: title })), _jsx(Styled.Content, { size: size, activeColorScheme: activeColorScheme, children: children ?? content })] }), !shouldAutoDismiss && isDismissible && (_jsx(Styled.DismissButton, { onClick: onDismiss, "aria-label": stringMap.ariaLabelDismissButton, children: _jsx(CloseIcon, { palette: currentPalette, color: "base", size: 16, "aria-hidden": "true" }) }))] }));
});
MessageBanner.displayName = componentName;
MessageBanner.defaultProps = {
    autoDismissDelay: 4000,
    level: "neutral",
    size: "default",
    type: defaultType
};
